import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ExternalLink,
  Globe,
  Loader2,
  RefreshCw,
  TrendingUp,
  UserRound,
  UserX,
  Users,
} from 'lucide-react';
import {
  formatAnonymousDistinctId,
  formatPathsPreview,
  formatSiteAnalyticsRelativeWhen,
  formatSiteAnalyticsWhen,
  loadSiteAnalytics,
  SITE_ANALYTICS_CHART_PERIOD_OPTIONS,
  type SiteAnalyticsAnonymousVisitor,
  type SiteAnalyticsChartPeriodHours,
  type SiteAnalyticsResult,
  type SiteAnalyticsVisitor,
} from '../../lib/posthogSiteActivity';
import {
  persistSiteAnalyticsPeriodHours,
  readSiteAnalyticsPeriodHours,
  siteAnalyticsPeriodLabel,
} from '../../lib/siteAnalyticsPeriod';
import SiteTrafficTrendChart from './SiteTrafficTrendChart';

function SummaryCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: number;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="mt-2 text-3xl font-semibold tabular-nums text-gray-900 dark:text-white">
        {value.toLocaleString()}
      </p>
      {hint ? (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      ) : null}
    </div>
  );
}

function SeenCell({ iso }: { iso: string }) {
  const relative = formatSiteAnalyticsRelativeWhen(iso);

  return (
    <div>
      <div>{formatSiteAnalyticsWhen(iso)}</div>
      {relative ? (
        <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{relative}</div>
      ) : null}
    </div>
  );
}

function KnownVisitorRow({ visitor }: { visitor: SiteAnalyticsVisitor }) {
  const displayName = visitor.name?.trim() || visitor.email || 'Unknown visitor';
  const pathsTitle = visitor.paths.join(', ') || '—';

  return (
    <tr className="text-gray-800 dark:text-gray-200">
      <td className="px-4 py-3 align-top">
        <div className="font-medium text-gray-900 dark:text-white">{displayName}</div>
        {visitor.email ? (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{visitor.email}</div>
        ) : null}
        {!visitor.inCrm ? (
          <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">
            Identified in PostHog, not matched to CRM
          </div>
        ) : null}
      </td>
      <td className="px-4 py-3 align-top tabular-nums">{visitor.pageViews}</td>
      <td
        className="px-4 py-3 align-top max-w-[320px] truncate text-xs text-gray-600 dark:text-gray-300"
        title={pathsTitle}
      >
        {formatPathsPreview(visitor.paths)}
      </td>
      <td className="px-4 py-3 align-top whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">
        <SeenCell iso={visitor.firstSeen} />
      </td>
      <td className="px-4 py-3 align-top whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">
        <SeenCell iso={visitor.lastSeen} />
      </td>
      <td className="px-4 py-3 align-top">
        {visitor.subscriberId && visitor.inCrm ? (
          <Link
            to={`/subscribers/${visitor.subscriberId}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-300 hover:underline"
          >
            Open contact
          </Link>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
    </tr>
  );
}

function AnonymousVisitorRow({ visitor }: { visitor: SiteAnalyticsAnonymousVisitor }) {
  const pathsTitle = visitor.paths.join(', ') || '—';
  const displayId = formatAnonymousDistinctId(visitor.distinctId);

  return (
    <tr className="text-gray-800 dark:text-gray-200">
      <td className="px-4 py-3 align-top">
        <div className="font-mono text-xs text-gray-900 dark:text-white" title={visitor.distinctId}>
          {displayId}
        </div>
        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Anonymous browser</div>
      </td>
      <td className="px-4 py-3 align-top tabular-nums">{visitor.pageViews}</td>
      <td
        className="px-4 py-3 align-top max-w-[320px] truncate text-xs text-gray-600 dark:text-gray-300"
        title={pathsTitle}
      >
        {formatPathsPreview(visitor.paths)}
      </td>
      <td className="px-4 py-3 align-top whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">
        <SeenCell iso={visitor.firstSeen} />
      </td>
      <td className="px-4 py-3 align-top whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">
        <SeenCell iso={visitor.lastSeen} />
      </td>
    </tr>
  );
}

export default function SiteAnalyticsView() {
  const [periodHours, setPeriodHours] = useState<SiteAnalyticsChartPeriodHours>(() =>
    readSiteAnalyticsPeriodHours(),
  );
  const [data, setData] = useState<SiteAnalyticsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handlePeriodChange = (hours: SiteAnalyticsChartPeriodHours) => {
    setPeriodHours(hours);
    persistSiteAnalyticsPeriodHours(hours);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await loadSiteAnalytics(periodHours);
      setData(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load site analytics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [periodHours]);

  useEffect(() => {
    void load();
  }, [load]);

  const periodLabel = siteAnalyticsPeriodLabel(periodHours);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Globe className="w-6 h-6 text-emerald-500" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Site analytics</h1>
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 max-w-3xl">
            PostHog traffic for stephansmith.me. Anonymous visitors are counted by browser id;
            known visitors are people PostHog could tie to an email after sign-in.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <label
            htmlFor="site-analytics-period"
            className="text-xs font-medium text-gray-600 dark:text-gray-400"
          >
            Period
          </label>
          <select
            id="site-analytics-period"
            value={periodHours}
            onChange={(event) =>
              handlePeriodChange(Number(event.target.value) as SiteAnalyticsChartPeriodHours)
            }
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100"
          >
            {SITE_ANALYTICS_CHART_PERIOD_OPTIONS.map((option) => (
              <option key={option.hours} value={option.hours}>
                {option.label}
              </option>
            ))}
          </select>

          {data?.posthogProjectUrl ? (
            <a
              href={data.posthogProjectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ExternalLink className="w-4 h-4" />
              Open PostHog
            </a>
          ) : null}

          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center justify-center p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            aria-label="Refresh site analytics"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && !data ? (
        <div className="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 p-6 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      ) : (
        <>
          <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide inline-flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-sky-500" />
                Traffic trend
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Page views on stephansmith.me · anonymous vs identified · {periodLabel}
              </p>
            </div>

            {loading && !data?.trend ? (
              <div className="py-16 flex justify-center">
                <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
              </div>
            ) : error ? (
              <div className="px-6 py-8 text-sm text-red-700 dark:text-red-300">{error}</div>
            ) : data?.trend ? (
              <div className="p-3 sm:p-4">
                <SiteTrafficTrendChart trend={data.trend} trendPeriodHours={periodHours} />
              </div>
            ) : (
              <div className="px-6 py-10 text-sm text-gray-500 dark:text-gray-400 text-center">
                No trend data available for this period.
              </div>
            )}
          </section>

          {loading && !data ? (
            <div className="py-16 flex justify-center">
              <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
            </div>
          ) : data ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <SummaryCard
                  label="Anonymous visitors"
                  value={data.summary.anonymousVisitors}
                  hint="Distinct browsers without an identified email"
                />
                <SummaryCard
                  label="Known visitors"
                  value={data.summary.knownVisitors}
                  hint="Distinct people with email in PostHog"
                />
                <SummaryCard
                  label="Anonymous page views"
                  value={data.summary.anonymousPageViews}
                  hint={periodLabel}
                />
                <SummaryCard
                  label="Known page views"
                  value={data.summary.knownPageViews}
                  hint={periodLabel}
                />
              </div>

              <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide inline-flex items-center gap-2">
                      <Users className="w-4 h-4 text-emerald-500" />
                      Known visitors
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Identified people who viewed the site in {periodLabel.toLowerCase()}.
                    </p>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                    {data.visitors.length.toLocaleString()} listed
                  </div>
                </div>

                {data.visitors.length === 0 ? (
                  <div className="p-10 text-sm text-gray-500 dark:text-gray-400 text-center">
                    No identified visitors in this period.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium">Person</th>
                          <th className="text-left px-4 py-2 font-medium">Page views</th>
                          <th className="text-left px-4 py-2 font-medium">Pages</th>
                          <th className="text-left px-4 py-2 font-medium">First seen</th>
                          <th className="text-left px-4 py-2 font-medium">Last seen</th>
                          <th className="text-left px-4 py-2 font-medium">CRM</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {data.visitors.map((visitor) => (
                          <KnownVisitorRow
                            key={`${visitor.subscriberId ?? visitor.email ?? visitor.lastSeen}-${visitor.firstSeen}`}
                            visitor={visitor}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide inline-flex items-center gap-2">
                      <UserX className="w-4 h-4 text-sky-500" />
                      Anonymous visitors
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Browsers without a signed-in identity in {periodLabel.toLowerCase()}. PostHog
                      distinct id shown (truncated).
                    </p>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                    {data.anonymousVisitors.length.toLocaleString()} listed
                    {data.summary.anonymousVisitors > data.anonymousVisitors.length
                      ? ` · top ${data.anonymousVisitors.length} of ${data.summary.anonymousVisitors.toLocaleString()}`
                      : ''}
                  </div>
                </div>

                {data.anonymousVisitors.length === 0 ? (
                  <div className="p-10 text-sm text-gray-500 dark:text-gray-400 text-center">
                    No anonymous page views in this period.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium">Browser id</th>
                          <th className="text-left px-4 py-2 font-medium">Page views</th>
                          <th className="text-left px-4 py-2 font-medium">Pages</th>
                          <th className="text-left px-4 py-2 font-medium">First seen</th>
                          <th className="text-left px-4 py-2 font-medium">Last seen</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {data.anonymousVisitors.map((visitor) => (
                          <AnonymousVisitorRow
                            key={`${visitor.distinctId}-${visitor.firstSeen}`}
                            visitor={visitor}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <p className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5">
                <UserRound className="w-3.5 h-3.5" />
                Total page views in period: {data.summary.totalPageViews.toLocaleString()} on
                stephansmith.me (`app: content`).
              </p>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
